/**
 * PORTED — RO-11-02 / #44587.
 * Origin: `excellent/app/_chrome/top-nav-client.tsx`.
 *
 * Changes:
 *   - next/navigation → @excellent/ui/router (usePathname, useNavigate, useSearch)
 *   - setScopeCookie server action → apiCall(scope.set) (RO-2-08 contract)
 *   - ScopedSearchButton in the right cluster (search landmark; fires open-search
 *     for the dock when it lands — same event PageHeader / ⌘K use)
 *   - Scope type local (no server-only @/scope)
 *   - ScopeProjectOption / FolderOption shapes match ShellData
 */

import { useState, useTransition } from "react";
import {
  usePathname,
  useNavigate,
  useSearch,
} from "@excellent/ui/router";
import { Check, ChevronDown, Folder, LayoutGrid, Star } from "@excellent/ui/icons";
import {
  Button,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TooltipProvider,
  ScopedSearchButton,
} from "@excellent/ui";
import { apiCall } from "@excellent/ui/api";
import { scope } from "@excellent/ui/api/contracts/scope";
import { DesktopUpdatePill } from "@excellent/ui/blocks/chrome/desktop-updater";
import { toTitleCase } from "@/projects/utils";
import { projectSlugFromPath } from "@/modes/project-path";
import { PlayButton } from "./play-button";
import { ActivityLogo } from "./activity-logo";
import { UserMenu } from "./user-menu";
import type { Scope } from "./scope-context";
import { routeForScope, type ScopeTarget } from "./route-for-scope";

/** Project row in the switcher — matches ShellScopeProject / ScopeProjectOption. */
export interface ScopeProjectOption {
  id: number;
  slug: string;
  name: string;
  group_id: number | null;
  parent_id: number | null;
  role: string | null;
}

export interface FolderOption {
  id: number;
  name: string;
}

interface TopNavClientProps {
  projects: ScopeProjectOption[];
  allProjects: { slug: string; name: string }[];
  groups: FolderOption[];
  workspaceName: string;
  defaultSlug: string | null;
  reviewCount: number;
  enabledSections: { slug: string; name: string; description?: string }[];
  totalSections: number;
  copilotEnabled: boolean;
}

function scopeFromPathname(
  pathname: string,
  allProjects: { slug: string; name: string }[],
): Scope {
  const slug = projectSlugFromPath(pathname);
  if (slug) {
    const project = allProjects.find((p) => p.slug === slug);
    if (project) {
      return { kind: "project", slug: project.slug, name: project.name };
    }
  }
  return { kind: "all" };
}

/**
 * The global top nav — full-width three-zone bar:
 *   LEFT  — logo
 *   CENTER — project switcher
 *   RIGHT — search · play · user
 */
export function TopNavClient({
  projects,
  allProjects,
  defaultSlug,
  reviewCount,
  copilotEnabled,
}: TopNavClientProps) {
  const pathname = usePathname();
  const scopeNow = scopeFromPathname(pathname, allProjects);
  return (
    <header
      className={cn(
        "absolute inset-x-0 top-0 z-40 flex h-[var(--nav-clearance)] items-center justify-between gap-3 px-4",
        "bg-background",
        "app-titlebar-drag",
      )}
    >
      <TooltipProvider delayDuration={300}>
        <div className="pointer-events-none absolute inset-x-0 top-1/2 hidden -translate-y-1/2 justify-center px-4 md:flex">
          <div className="pointer-events-auto flex min-w-0 items-center">
            <ProjectSwitcher
              scope={scopeNow}
              projects={projects}
              allProjects={allProjects}
              defaultSlug={defaultSlug}
            />
          </div>
        </div>

        <div className="nav-left-cluster flex min-w-0 shrink-0 items-center gap-1">
          <ActivityLogo />
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <DesktopUpdatePill />
          {/* Search affordance — focuses the dock via open-search when present;
              aria-label "Search" satisfies the chrome landmark probe. */}
          <ScopedSearchButton label="Search" />
          <PlayButton />
          <UserMenu reviewCount={reviewCount} copilotEnabled={copilotEnabled} />
        </div>
      </TooltipProvider>
    </header>
  );
}

function ProjectSwitcher({
  scope: activeScope,
  projects,
  allProjects,
  defaultSlug,
}: {
  scope: Scope;
  projects: ScopeProjectOption[];
  allProjects: { slug: string; name: string }[];
  defaultSlug: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [, startTransition] = useTransition();
  const router = useNavigate();
  const pathname = usePathname();
  const searchParams = useSearch();
  const searchString = searchParams.toString();

  const label = activeScope.kind === "all" ? "All projects" : toTitleCase(activeScope.name);
  const activeSlug = activeScope.kind === "project" ? activeScope.slug : null;

  const switchTo = (target: ScopeTarget) => {
    setOpen(false);
    startTransition(() => {
      const slug = target.kind === "project" ? target.slug : null;
      // Cookie soft-hint via RO-2-08 contract (was setScopeCookie server action).
      void apiCall(scope.set, { body: { slug } }).catch(() => {
        /* soft hint — navigation still proceeds */
      });
      router.push(
        routeForScope(pathname, searchString ? `?${searchString}` : "", target),
      );
    });
  };

  const needle = q.trim().toLowerCase();
  const matches = needle
    ? allProjects.filter((p) => p.name.toLowerCase().includes(needle))
    : [];
  const idle: { slug: string; name: string }[] = projects;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQ("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Project: ${label} — switch`}
          className="flex h-6 max-w-[220px] items-center gap-1.5 rounded-md px-1 type-body text-muted-foreground outline-none transition-colors hover:bg-foreground/[0.06] hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring data-[state=open]:bg-foreground/[0.06] data-[state=open]:text-foreground"
        >
          <Folder strokeWidth={1.75} className="size-3.5 shrink-0" />
          <span className="truncate">{label}</span>
          <ChevronDown strokeWidth={1.75} className="size-3 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-[300px] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            value={q}
            onValueChange={setQ}
            placeholder="Find a project…"
          />
          <CommandList>
            <CommandEmpty>No projects found.</CommandEmpty>
            <CommandGroup heading={needle ? "Matches" : "Recent"}>
              {(needle ? matches : idle).map((p) => (
                <CommandItem
                  key={p.slug}
                  value={p.slug}
                  onSelect={() => switchTo({ kind: "project", slug: p.slug })}
                >
                  <Folder strokeWidth={1.75} className="size-3.5 text-muted-foreground" />
                  <span className="flex-1 truncate">{toTitleCase(p.name)}</span>
                  {p.slug === activeSlug ? (
                    <Check strokeWidth={1.75} className="size-3.5 text-foreground" />
                  ) : p.slug === defaultSlug ? (
                    <Star
                      strokeWidth={1.75}
                      className="size-3.5 fill-current text-muted-foreground"
                    />
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                value="__all_projects__"
                onSelect={() => {
                  setOpen(false);
                  router.push("/projects");
                }}
              >
                <LayoutGrid strokeWidth={1.75} className="size-3.5 text-muted-foreground" />
                <span className="flex-1">All projects</span>
                <span className="type-subtext text-muted-foreground/70">manage &amp; map</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
